import { AppDirectClient, type FetchFn } from './client.js';
import { resolveConfig, validateConfig } from './config.js';
import { buildLoginRedirect } from './flows/login.js';
import { handleCallback } from './flows/callback.js';
import { refreshSession } from './flows/refresh.js';
import { getMe, getUserFromSession } from './flows/me.js';
import { getFeJwt } from './flows/jwt.js';
import { buildLogout } from './flows/logout.js';
import { importSession } from './flows/import-session.js';
import { ensureValidFeJwt } from './session/ensure-fejwt.js';
import { createRefreshCoordinator } from './session/refresh-coordinator.js';
import type { AppDirectAuth, AppDirectAuthConfig } from './types.js';

export function createAppDirectAuth(
  configInput: AppDirectAuthConfig,
  options?: { fetch?: FetchFn },
): AppDirectAuth {
  const config = resolveConfig(configInput);
  validateConfig(config);
  const client = new AppDirectClient(config, options?.fetch ?? fetch);
  const coordinator = createRefreshCoordinator();

  const sessionInput = (input: {
    sessionToken: string | null | undefined;
    refreshToken?: string | null;
  }) => ({
    sessionToken: input.sessionToken ?? null,
    refreshToken: input.refreshToken ?? null,
  });

  return {
    config,
    buildLoginRedirect: () => buildLoginRedirect(config, client),
    handleCallback: (input) => handleCallback(config, client, input),
    refreshSession: (input) => refreshSession(config, client, coordinator, input),
    getMe: (input) => getMe(config, client, coordinator, input),
    getUserFromSession: (input) => getUserFromSession(input),
    ensureValidFeJwt: (input) =>
      ensureValidFeJwt(config, client, coordinator, sessionInput(input)),
    getFeJwt: (input) => getFeJwt(config, client, coordinator, input),
    importSession: (input) => importSession(config, client, input),
    buildLogout: () => buildLogout(config),
  };
}

export { AppDirectClient } from './client.js';
export { resolveConfig, validateConfig } from './config.js';
export { AuthError } from './errors.js';
export { ensureValidFeJwt } from './session/ensure-fejwt.js';
export { RefreshCoordinator, createRefreshCoordinator } from './session/refresh-coordinator.js';
export type {
  AppDirectAuth,
  AppDirectAuthConfig,
  AuthUser,
  CallbackResult,
  CookieMutation,
  CookieStore,
  JwtResult,
  LoginRedirectResult,
  LogoutResult,
  MeResult,
  RefreshResult,
  ResolvedConfig,
  RoutePaths,
} from './types.js';
export {
  COOKIE_OAUTH_STATE,
  COOKIE_REFRESH_TOKEN,
  COOKIE_SESSION,
} from './constants.js';
